import {
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { neutral } from '@ahora-patagonia/design-tokens';
import type { PublicShell } from '@ahora-patagonia/contracts';
import { IconoBuscar, IconoCerrar } from '../modules/shared/components/iconos.js';

export interface NavDrawerProps {
  shell: PublicShell | null;
  abierta: boolean;
  onCerrar: () => void;
}

/**
 * El cajón de navegación (handoff §2): muestra **todas** las secciones con su cantidad de
 * notas — una tira horizontal escondería las últimas sin avisar, y en un diario regional la
 * última categoría puede ser la más importante del pueblo. Filas de 56 px, buscador de
 * 48 px arriba, cuesta un toque.
 *
 * Todas quiere decir **todas**, también las que la redacción no eligió para la fila de la cabecera:
 * el toggle cura la cabecera, no esconde secciones. Es lo que hace que "Más" tenga qué mostrar y que
 * apagar una sección de la fila no la deje inalcanzable en teléfono, donde este cajón *es* el menú.
 *
 * `keepMounted: false`: el cajón no existe en el HTML del server ni en el primer render del
 * cliente — se abre solo por interacción, así que no puede desincronizar la hidratación.
 */
export function NavDrawer({ shell, abierta, onCerrar }: NavDrawerProps) {
  const navigate = useNavigate();
  const secciones = shell?.nav ?? [];

  return (
    <Drawer
      anchor="left"
      open={abierta}
      onClose={onCerrar}
      ModalProps={{ keepMounted: false }}
      PaperProps={{ sx: { width: 300, maxWidth: '85vw' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', height: 64, px: 1 }}>
        <IconButton aria-label="Cerrar la navegación" onClick={onCerrar}>
          <IconoCerrar />
        </IconButton>
        <Typography sx={{ fontWeight: 800, ml: 0.5 }}>{shell?.tenant.nombre ?? ''}</Typography>
      </Box>

      <Box
        component="form"
        role="search"
        sx={{ px: 2, pb: 1.5 }}
        onSubmit={(e) => {
          e.preventDefault();
          const q = new FormData(e.currentTarget).get('q');
          onCerrar();
          navigate(q ? `/buscar?q=${encodeURIComponent(String(q))}` : '/buscar');
        }}
      >
        <OutlinedInput
          name="q"
          fullWidth
          placeholder="Buscar"
          inputProps={{ 'aria-label': 'Buscar' }}
          endAdornment={
            <InputAdornment position="end">
              <IconoBuscar />
            </InputAdornment>
          }
        />
      </Box>

      <List component="nav" aria-label="Secciones" sx={{ py: 0 }}>
        {secciones.map((s) => (
          <ListItem key={s.key} disablePadding sx={{ borderTop: `1px solid ${neutral[300]}` }}>
            <ListItemButton
              component={RouterLink}
              to={s.path}
              onClick={onCerrar}
              sx={{ minHeight: 56, justifyContent: 'space-between', px: 2 }}
            >
              <Typography component="span" sx={{ fontWeight: 800, fontSize: 15 }}>
                {s.name}
              </Typography>
              {/* Sin conteo en Última hora y Más leídas: "todas las del medio" al lado de una
                  categoría con 12 invitaría a compararlas, y no son lo mismo. */}
              {s.articleCount !== null && (
                <Typography component="span" sx={{ color: neutral[600], fontSize: 13 }}>
                  {s.articleCount}
                </Typography>
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
}
